import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { AnnouncementWithStatus } from "../../../graphql/generated/graphql";

interface StudentAnnouncementCardProps {
  announcement: AnnouncementWithStatus;
  isInGrid?: boolean;
}

const StudentAnnouncementCard: React.FC<StudentAnnouncementCardProps> = ({ announcement, isInGrid = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isNew = announcement.isNew ?? false;

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();
  const stripHtmlTags = (html: string) => html.replace(/<[^>]*>/g, "");

  const handleReadMoreToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const messageText = stripHtmlTags(announcement.message);
  const shouldShowReadMore = messageText.length > 150;

  return (
    <View style={[styles.announcementCard, isInGrid && !isExpanded && styles.gridCard]}>
      <View style={styles.cardHeader}>
        <View style={styles.cardHeaderLeft}>
          {isNew && (
            <View style={styles.newBadge}>
              <CustomText
                text="New"
                size="xs"
                weight="ultraBold"
                style={styles.newBadgeText}
                useTranslationText={false}
              />
            </View>
          )}
          <CustomText
            text={announcement.title}
            size="s"
            weight="ultraBold"
            style={[styles.announcementTitle, isNew && styles.titleWithBadge]}
            useTranslationText={false}
          />
        </View>
        <CustomText
          text={formatDate(announcement.created)}
          size="s"
          weight="medium"
          style={styles.dateText}
          useTranslationText={false}
        />
      </View>

      <CustomSpacing type="vertical" size="m" />

      <View style={[styles.messageContainer, isInGrid && !isExpanded && styles.messageContainerGrid]}>
        <CustomText
          text={isExpanded || !shouldShowReadMore ? messageText : `${messageText.substring(0, 150)}...`}
          size="s"
          style={styles.messageText}
          useTranslationText={false}
        />
      </View>

      {shouldShowReadMore && (
        <>
          <CustomSpacing type="vertical" size="m" />
          <TouchableOpacity onPress={handleReadMoreToggle} style={styles.readMoreButton}>
            <CustomText
              text={isExpanded ? "Read Less" : "Read More"}
              size="s"
              style={styles.readMoreText}
              useTranslationText={false}
            />
          </TouchableOpacity>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  announcementCard: {
    backgroundColor: "#FFF",
    borderRadius: 20,
    padding: 20,
    width: "100%",
  },
  gridCard: {
    height: 150,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  cardHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginRight: 12,
  },
  newBadge: {
    backgroundColor: "#0084FF",
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 10,
    flexShrink: 0,
  },
  newBadgeText: {
    color: "#FFFFFF",
    fontSize: 10,
  },
  announcementTitle: {
    color: "#344054",
    flex: 1,
  },
  titleWithBadge: {
    flex: 1,
  },
  dateText: {
    color: "#667085",
    flexShrink: 0,
  },
  messageContainer: {
    flex: 1,
  },
  messageContainerGrid: {
    maxHeight: 40,
    overflow: "hidden",
  },
  messageText: {
    color: "#667085",
    lineHeight: 20,
  },
  readMoreButton: {
    alignSelf: "flex-start",
  },
  readMoreText: {
    color: "#3E68FE",
    fontWeight: "600",
  },
});

export default StudentAnnouncementCard;
